import { HttpEvent, HttpHandler, HttpHeaders, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { KeycloakService } from '../../keycloak/keycloak.service';
import { ModuleConfigurationService } from '../../services/configuration.service';
import { AuthenticationService } from '../authentication/auth.service';
import { Context as LocalStorageContext } from '../context.local-storage';

@Injectable({ providedIn: 'root' })
export class JwtInterceptor implements HttpInterceptor {
  private jwtHelperService: JwtHelperService = new JwtHelperService();
  private authService: AuthenticationService;
  private configuration: ModuleConfigurationService;
  private useBearer: boolean;
  private clientHeaderName = 'client-name';

  constructor(private keyCloakService: KeycloakService, private injector: Injector) {
    this.configuration = this.injector.get(ModuleConfigurationService);
    if (this.configuration.options) {
      this.useBearer = this.configuration.options.useBearer && this.configuration.options.useBearer === true;

      this.clientHeaderName = this.configuration.options.clientHeaderName
        ? this.configuration.options.clientHeaderName
        : this.clientHeaderName;
    }
  }

  private getAuthService(): AuthenticationService {
    if (this.authService == null) {
      this.authService = this.injector.get(AuthenticationService);
    }
    return this.authService;
  }

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<any>> {
    const authHeader$ =
      this.keyCloakService.getKeycloakSetting() && this.keyCloakService.getKeycloakSetting().enabled
        ? this.getKeycloakHeaders(req.headers, req)
        : this.getJwtHeaders(req.headers, req);

    return authHeader$.pipe(
      switchMap(headers => {
        const authReq = req.clone({ headers });
        return next.handle(authReq).pipe(
          catchError((response: HttpResponse<any>) => {
            if (response && response.status === 401) {
              this.getAuthService().logout();
            }
            throw response;
          })
        );
      })
    );
  }

  public getHttpHeaders(headers: HttpHeaders): HttpHeaders {
    if (headers.get('Accept') !== '*/*') {
      let newHeaders = headers.set('Content-Type', 'application/json');
      newHeaders = newHeaders.set('Accept', 'application/json');
      return headers;
    } else {
      return headers;
    }
  }

  public getJwtHeaders(headers: HttpHeaders, req: HttpRequest<any>): Observable<HttpHeaders> {
    let newHeaders = this.getHttpHeaders(headers);
    if (this.checkUrl(req)) {
      return of(newHeaders);
    }
    const token = localStorage.getItem('id_token');
    const clientName = localStorage.getItem('current_client_name');
    const userPass = localStorage.getItem('userPass');
    const hasValidToken = token != null && !this.jwtHelperService.isTokenExpired(token);

    if (clientName) {
      newHeaders = headers.set(this.clientHeaderName, clientName);
    }

    if (hasValidToken) {
      if (this.useBearer) {
        newHeaders = newHeaders.append('Authorization', 'Bearer ' + token);
      } else {
        newHeaders = newHeaders.append('Authorization', 'Basic ' + userPass);
      }
    } else {
      newHeaders = newHeaders.delete('Authorization');
    }

    return of(newHeaders);
  }

  public checkUrl(req: HttpRequest<any>): boolean {
    return (
      req.url.indexOf('assets/i18n') !== -1 ||
      req.url.indexOf('realms') !== -1 ||
      req.url.indexOf('api/authentication') !== -1 ||
      req.url.indexOf('frontend/keycloak') !== -1
    );
  }

  public getKeycloakHeaders(headers: HttpHeaders, req: HttpRequest<any>): Observable<HttpHeaders> {
    let newHeaders = this.getHttpHeaders(headers);

    if (this.checkUrl(req)) {
      return of(newHeaders);
    }

    return this.getAuthService()
      .isLoggedIn()
      .pipe(
        map(isLoggedIn => {
          const accessToken = this.getAuthService().getAccessToken();
          if (isLoggedIn && accessToken) {
            if (req.url.indexOf('api/clients') != -1) {
              newHeaders = newHeaders.append('Authorization', 'Bearer ' + accessToken);
            } else {
              newHeaders = newHeaders.append('Authorization', 'Bearer ' + accessToken);
              const clientName = localStorage.getItem(LocalStorageContext.KEY_CURRENT_CLIENT_NAME);
              if (clientName) {
                newHeaders = newHeaders.append(this.clientHeaderName, clientName);
              }
            }
          }
          return newHeaders;
        })
      );
  }
}
